import React from 'react'

import GoogleAnalytics from 'react-ga4'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import styled from 'styled-components'

import { type Locale, useHtmlLang } from '@libs/typescript-utils'

import { Api, authActions, dashboardActions } from '@services'

import { bindActionToPromise, cancellablePromise, parseApiErrorMessage } from '@helpers'

import { useCancellablePromiseRef } from '@hooks/useCancellablePromiseRef'

import { PublicPage } from '@components/layouts'
import { Button, CircularProgress, Grid, Typography } from '@components/ui'

import { SessionExpiredDialog } from './SessionExpiredDialog'

import { ReloadPageButtonMessage } from '@messages'

const StyledGrid = styled(Grid)`
  gap: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

interface InitializeApplicationProps {
  children: React.ReactNode
  fetch: AsyncFunction<void, BackendInitResponse>
  locale: Locale
  setMaintenanceMode: (isMaintenanceMode: boolean) => void
}

function PureInitializeApplication({ children, fetch, locale, setMaintenanceMode }: InitializeApplicationProps) {
  useHtmlLang(locale)
  const [{ error, fetched }, setState] = React.useState<{
    error: BackendError
    fetched: boolean
  }>({
    fetched: false,
    error: null,
  })
  const cPromiseRef = useCancellablePromiseRef<BackendInitResponse>()

  React.useEffect(() => {
    async function init() {
      try {
        cPromiseRef.current = cancellablePromise(fetch())
        const response = await cPromiseRef.current.promise
        setState(state => ({ ...state, fetched: true }))
        // initialize GoogleAnalytics
        if (response.ga_api_key) {
          GoogleAnalytics.initialize(response.ga_api_key)
        }
      } catch (error) {
        const errorMessage = parseApiErrorMessage(error)
        if (errorMessage) {
          setState(state => ({ ...state, error: errorMessage }))
        }
      }
    }
    init()
  }, [])

  //* maintenance control
  React.useEffect(() => {
    const interceptor = Api.interceptors.response.use(
      function (response) {
        return response
      },
      function (error) {
        if (error.response?.status === 423) {
          setMaintenanceMode(true)
        }
        return Promise.reject(error)
      }
    )
    return () => Api.interceptors.response.eject(interceptor)
  }, [setMaintenanceMode])

  function handleReloadWindow() {
    window.location.reload()
  }

  if (fetched) {
    return (
      <>
        {children}
        <SessionExpiredDialog />
      </>
    )
  }

  // show error or loading screen
  return (
    <PublicPage hideNavbar>
      <Grid container justifyContent="center" alignItems="center">
        {error ? (
          <StyledGrid item>
            <Typography size="heading-1" color="gray-70">
              <FormattedMessage id="pages.initError.title" defaultMessage="Hiba" />
            </Typography>

            <Typography size="400-lg" color="gray-80">
              <FormattedMessage id="pages.initError.text" defaultMessage="Az applikáció betöltése sikertelen. " />
            </Typography>

            <Button variant="primaryText" onClick={handleReloadWindow}>
              {ReloadPageButtonMessage}
            </Button>
          </StyledGrid>
        ) : (
          <StyledGrid item>
            <div style={{ marginBottom: 30 }}>
              <CircularProgress size={50} />
            </div>
            <Typography size="400-lg" color="gray-80">
              <FormattedMessage id="pages.initialize.app.title" defaultMessage="Applikáció betöltése folyamatban..." />
            </Typography>
          </StyledGrid>
        )}
      </Grid>
    </PublicPage>
  )
}

export const InitializeApplication = connect(
  (state: Store) => ({
    locale: state.locale.language, // trigger language update in app
  }),
  dispatch => ({
    fetch: bindActionToPromise(dispatch, dashboardActions.initialize.request),
    setMaintenanceMode: bindActionCreators(authActions.setMaintenanceMode.request, dispatch),
  })
)(PureInitializeApplication)

InitializeApplication.displayName = 'InitializeApplication'
